import gsap from '../../vendor/js/gsap.min.js';
import ScrollTrigger from '../../vendor/js/ScrollTrigger.min.js';

class PinScroller {
    constructor() {
        this.sections = document.querySelectorAll('.cc-pin_scroller');
        this.breakpoint = 992;
        this.scrollTriggers = [];
        this.marqueeAnimations = [];
        this.previousWidth = window.innerWidth;

        if (this.sections.length) {
            this.init();
            this.handleResize();
        }
    }

    init() {
        this.sections.forEach(section => {
            const pinImg = section.querySelector('.cc-pin_img');
            const metricsContainer = section.querySelector('.c-metrics_cont');
            const metrics = section.querySelectorAll('.c-metric_item');

            if (window.innerWidth < this.breakpoint) {
                // Create and immediately play the marquee animation
                const marqueeAnim = this.setupMobileMarquee(metricsContainer, metrics);
                if (marqueeAnim) marqueeAnim.play();
            } else {
                this.setupDesktopPin(section, pinImg);
            }
        });
    }

    setupMobileMarquee(container, metrics) {
        if (!container) return null;
        
        // Get the inner container
        const innerContainer = container.querySelector('.c-metrics_cont_inner');
        if (!innerContainer) return null;

        // Clean up any existing animations for this container
        this.cleanupMarquee(container);

        // Clone the inner container
        const clone = innerContainer.cloneNode(true);
        clone.classList.add('clone');
        container.appendChild(clone);

        // Get computed styles for the clone
        const cloneStyle = window.getComputedStyle(clone);
        const paddingLeft = parseInt(cloneStyle.paddingLeft, 10);

        // Get the width of one complete set (original inner container)
        const containerWidth = innerContainer.offsetWidth;
        
        // Total distance to animate (container width + padding)
        const totalDistance = containerWidth + paddingLeft;

        // Create the animation
        const pixelsPerSecond = 50; // Adjust speed as needed
        const duration = totalDistance / pixelsPerSecond;

        const marqueeAnim = gsap.timeline({
            repeat: -1,
            paused: true,
            defaults: { ease: "none" }
        }).to(container, {
            x: -totalDistance,
            duration: duration,
            ease: "none",
            onComplete: function() {
                gsap.set(container, { x: 0 });
            }
        });

        // Store animation with reference to its container for easier cleanup
        this.marqueeAnimations.push({
            animation: marqueeAnim,
            container: container
        });

        return marqueeAnim;
    }

    setupDesktopPin(section, pinImg) {
        const trigger = ScrollTrigger.create({
            trigger: section,
            pin: pinImg,
            start: "top top",
            end: "bottom bottom"
        });
        
        this.scrollTriggers.push(trigger);
    }

    cleanupMarquee(container) {
        if (!container) return;
        
        // Find and kill animations for this specific container
        this.marqueeAnimations = this.marqueeAnimations.filter(item => {
            if (item.container === container) {
                if (item.animation) {
                    item.animation.pause();
                    item.animation.progress(0);
                    item.animation.kill();
                }
                return false;
            }
            return true;
        });

        // Remove clones and reset position
        const existingClones = container.querySelectorAll('.clone');
        existingClones.forEach(clone => clone.remove());
        gsap.set(container, { x: 0 });
    }

    cleanupPin(section) {
        // Kill associated ScrollTrigger
        const trigger = this.scrollTriggers.find(st => st.vars.trigger === section);
        if (trigger) {
            trigger.kill();
            this.scrollTriggers = this.scrollTriggers.filter(st => st !== trigger);
        }
    }

    handleResize() {
        let timeout;
        window.addEventListener('resize', () => {
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                const currentWidth = window.innerWidth;
                
                // Crossing from mobile to desktop
                if (currentWidth >= this.breakpoint && this.previousWidth < this.breakpoint) {
                    this.sections.forEach(section => {
                        const metricsContainer = section.querySelector('.c-metrics_cont');
                        const pinImg = section.querySelector('.cc-pin_img');
                        
                        // First cleanup mobile marquee
                        this.cleanupMarquee(metricsContainer);
                        
                        // Then setup desktop pin
                        this.setupDesktopPin(section, pinImg);
                    });
                }
                // Crossing from desktop to mobile
                else if (currentWidth < this.breakpoint && this.previousWidth >= this.breakpoint) {
                    this.sections.forEach(section => {
                        const metricsContainer = section.querySelector('.c-metrics_cont');
                        const metrics = section.querySelectorAll('.c-metric_item');
                        
                        // First cleanup desktop pin
                        this.cleanupPin(section);
                        
                        // Then setup and play mobile marquee
                        const marqueeAnim = this.setupMobileMarquee(metricsContainer, metrics);
                        if (marqueeAnim) marqueeAnim.play();
                    });
                }

                this.previousWidth = currentWidth;
            }, 250);
        });
    }

    destroy() {
        // Clean up all animations
        this.marqueeAnimations.forEach(item => {
            if (item.animation) item.animation.kill();
        });
        this.marqueeAnimations = [];

        // Clean up all ScrollTriggers
        this.scrollTriggers.forEach(trigger => trigger.kill());
        this.scrollTriggers = [];

        // Remove all clones
        this.sections.forEach(section => {
            const container = section.querySelector('.c-metrics_cont');
            if (container) {
                const clones = container.querySelectorAll('.clone');
                clones.forEach(clone => clone.remove());
                gsap.set(container, { x: 0 });
            }
        });
    }
}

export default PinScroller;